import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  type CorridorUnavailableReason,
  type PaymentCorridor,
  PaymentCorridorsService,
  type PaymentDirection,
} from "@/shared/services/payments/paymentCorridors.service";
import { type CorridorLimits, corridorLimitsOf } from "@/shared/utils/amountLimits";
import { createCorridorCatalogStore } from "@/shared/utils/corridorCatalogStore";
import {
  type CorridorScope,
  isCurrencyInMaintenance,
  isScopeInMaintenance,
} from "@/shared/utils/railMaintenance";

// Routes, fees and the rail switch are changed by hand, minutes apart at worst, and the backend already
// memoizes for 60 s. One read per organization serves every screen, and an open one reads again once its
// answer is this old.
const STALE_AFTER_MS = 2 * 60_000;

const NO_CORRIDORS: PaymentCorridor[] = [];

const catalogs = createCorridorCatalogStore(
  async (organizationId) => (await PaymentCorridorsService.list(organizationId)).corridors ?? [],
  STALE_AFTER_MS,
);

/**
 * Makes the screens showing a catalog read it again and drops the rest — call after staff edit a rail, a
 * route or a fee so the gating reflects it without a reload.
 */
export function invalidateCorridors(): void {
  catalogs.invalidate();
}

const corridorKey = (rail: string, country: string, currency: string, direction: PaymentDirection): string =>
  `${direction}:${country}:${currency}:${rail}`;

export interface UseAvailableCorridorsResult {
  corridors: PaymentCorridor[];
  isLoading: boolean;
  /** True until the first answer arrives, and forever if the endpoint is unreachable — see below. */
  isUnknown: boolean;
  /** Is this exact corridor runnable right now? */
  isAvailable: (rail: string, country: string, currency: string, direction: PaymentDirection) => boolean;
  /** Why this exact corridor cannot run — null while it can, or while the catalog is unknown. */
  reasonFor: (
    rail: string,
    country: string,
    currency: string,
    direction: PaymentDirection,
  ) => CorridorUnavailableReason | null;
  /** Any runnable corridor for this currency in this direction, whatever the rail. */
  hasCurrency: (currency: string, direction: PaymentDirection) => boolean;
  /** Rails that can run for a currency+direction — drives the method chips on a wallet card. */
  railsFor: (currency: string, direction: PaymentDirection) => string[];
  /**
   * Per-transaction bounds of this exact corridor, per payer kind — null while the catalog is unknown, when
   * the corridor is absent, or when it carries no bound at all. Null caps nothing client-side: the backend
   * rejects an out-of-range amount at preview/initiate (payins) and quote/create (payouts) regardless.
   */
  limitsFor: (
    rail: string,
    country: string,
    currency: string,
    direction: PaymentDirection,
  ) => CorridorLimits | null;
  /** Nothing in the scope runs and a switched-off rail is why: shown as maintenance. False while unknown. */
  inMaintenance: (scope: CorridorScope) => boolean;
  /** All its corridors are off: converting into it works, sending it out does not. False while unknown. */
  currencyInMaintenance: (currency: string) => boolean;
}

/**
 * Which corridors the current organization can actually operate on.
 *
 * **Fails open.** If the endpoint errors or has not answered yet, `isUnknown` is true and every predicate
 * returns true: a catalog that cannot be read must not black out a dashboard that worked yesterday. Callers
 * gate on `available === false`, never on "not present in a list I may not have". The same holds for the
 * amount limits: an unreadable catalog shows no hint and caps nothing — the backend is the guard.
 *
 * An INTERNAL reader is looking at another organization's data, so it must pass that org — commissions are
 * organization-scoped and so is the verdict. An organization not read yet, or whose read failed, is unknown:
 * the previous organization's catalog never stands in for it.
 *
 * **Stays fresh while open.** A screen reads the catalog again once its answer is two minutes old (while the
 * tab is visible) and when the tab comes back, and `invalidateCorridors()` makes every open screen read it at
 * once, keeping the last answer on screen meanwhile.
 */
export function useAvailableCorridors(organizationId?: string): UseAvailableCorridorsResult {
  const { user, isInternalUser } = useAuth();
  // Members never send an org — the server pins them. Staff send the org they are LOOKING AT, falling back
  // to their own: on a surface with no org selector (the payins page, a wallet card) an internal user is
  // acting on their own organization, which is exactly the org the payin payload is built for. Without the
  // fallback the hook never fetched for staff at all, and every corridor silently failed open.
  const scopedOrgId = isInternalUser
    ? (organizationId ?? user?.organization_id ?? user?.organization?.id ?? undefined)
    : undefined;
  const key = user ? (scopedOrgId ?? "self") : null;

  const subscribe = useCallback(
    (listener: () => void) => (key ? catalogs.subscribe(key, listener) : () => {}),
    [key],
  );
  const entry = useSyncExternalStore(subscribe, () => (key ? catalogs.get(key) : undefined));

  useEffect(() => {
    if (!key) return;
    const revalidate = () => {
      if (catalogs.isStale(key)) void catalogs.refresh(key, scopedOrgId);
    };
    const revalidateIfVisible = () => {
      if (document.visibilityState === "visible") revalidate();
    };
    revalidate();
    const staleIn = entry ? Math.max(0, entry.fetchedAt + STALE_AFTER_MS - Date.now()) : STALE_AFTER_MS;
    const timer = window.setTimeout(revalidateIfVisible, staleIn);
    document.addEventListener("visibilitychange", revalidateIfVisible);
    window.addEventListener("focus", revalidate);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", revalidateIfVisible);
      window.removeEventListener("focus", revalidate);
    };
  }, [key, scopedOrgId, entry]);

  const corridors = entry?.corridors ?? NO_CORRIDORS;
  const resolved = entry?.corridors != null;
  const isLoading = entry?.loading ?? false;

  const index = useMemo(() => {
    const byCorridor = new Map<string, PaymentCorridor>();
    for (const corridor of corridors) {
      byCorridor.set(
        corridorKey(corridor.rail, corridor.country, corridor.currency, corridor.direction),
        corridor,
      );
    }
    return byCorridor;
  }, [corridors]);

  return useMemo(() => {
    const isUnknown = !resolved;
    const runnable = (corridor: PaymentCorridor | undefined): boolean => corridor?.available === true;

    return {
      corridors,
      isLoading,
      isUnknown,
      isAvailable: (rail, country, currency, direction) =>
        isUnknown || runnable(index.get(corridorKey(rail, country, currency, direction))),
      reasonFor: (rail, country, currency, direction) =>
        isUnknown
          ? null
          : (index.get(corridorKey(rail, country, currency, direction))?.unavailable_reason ?? null),
      hasCurrency: (currency, direction) =>
        isUnknown ||
        corridors.some(
          (c) =>
            c.available && c.direction === direction && c.currency.toUpperCase() === currency.toUpperCase(),
        ),
      railsFor: (currency, direction) =>
        corridors
          .filter(
            (c) =>
              c.available && c.direction === direction && c.currency.toUpperCase() === currency.toUpperCase(),
          )
          .map((c) => c.rail),
      limitsFor: (rail, country, currency, direction) =>
        isUnknown
          ? null
          : corridorLimitsOf(index.get(corridorKey(rail, country, currency, direction))?.limits),
      inMaintenance: (scope) => !isUnknown && isScopeInMaintenance(corridors, scope),
      currencyInMaintenance: (currency) => !isUnknown && isCurrencyInMaintenance(corridors, currency),
    };
  }, [corridors, index, isLoading, resolved]);
}
